import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { SLIDES } from "@/data/content";
import { MaskLines, EASE } from "@/components/motion";

const DURATION = 7000;

export default function HeroSlider() {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [3, -3]), { stiffness: 60, damping: 20 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [-2, 2]), { stiffness: 60, damping: 20 });

  const go = useCallback((d) => setIdx((i) => (i + d + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    const t = setTimeout(() => go(1), DURATION);
    return () => clearTimeout(t);
  }, [idx, go]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const s = SLIDES[idx];

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-slider"
      className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#070A12] [perspective:1400px]"
    >
      <motion.div style={{ y: imgY, rotateX: rotX, rotateY: rotY }} className="absolute -inset-[4%] [transform-style:preserve-3d]">
        <AnimatePresence initial={false}>
          <motion.div
            key={idx}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.14, clipPath: "inset(0 0 0 100%)" }}
            animate={{ opacity: 1, scale: 1.02, clipPath: "inset(0 0 0 0%)" }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.2 }, clipPath: { duration: 1.4, ease: EASE }, scale: { duration: 8, ease: "linear" } }}
          >
            <img src={s.image} alt="" className="w-full h-full object-cover" data-testid={`hero-slide-image-${idx + 1}`} />
          </motion.div>
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#070A12] via-[#070A12]/75 to-[#070A12]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#070A12] via-transparent to-[#070A12]/60" />
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-[#6B2D8F]/25 blur-[140px] pointer-events-none" />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 h-full max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 flex flex-col justify-end pb-36 sm:pb-40">
        <div className="grid max-w-4xl">
          <AnimatePresence initial={true}>
            <motion.div key={idx} className="[grid-area:1/1]" exit={{ opacity: 0, y: -24, transition: { duration: 0.45 } }}>
              <motion.p
                className="eyebrow mb-6 flex items-center gap-3"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="w-10 h-px bg-[#C5A059]" /> {s.eyebrow}
              </motion.p>
              <h1 className="font-display font-medium text-slate-50 text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.4rem] tracking-tight" data-testid={`hero-slide-heading-${idx + 1}`}>
                <MaskLines lines={s.lines} onLoad />
              </h1>
              <motion.p
                className="mt-7 max-w-xl text-sm sm:text-lg text-slate-300 leading-relaxed"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6, ease: EASE }}
              >
                {s.sub}
              </motion.p>
              <motion.div
                className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.8, ease: EASE }}
              >
                <Link to={s.primary.to} data-testid="hero-cta-primary" className="btn-bronze group inline-flex items-center justify-center gap-3 px-7 py-4 text-sm font-medium tracking-wide">
                  {s.primary.label}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
                </Link>
                <Link
                  to={s.secondary.to}
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-3 px-7 py-4 text-sm tracking-wide text-slate-100 border border-white/25 hover:border-[#C5A059] hover:bg-white/5 transition-[border-color,background-color] duration-500"
                >
                  {idx === 0 && <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-red-500" />}
                  {s.secondary.label}
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      <div className="absolute z-20 bottom-0 inset-x-0 border-t border-white/10 bg-[#070A12]/40 backdrop-blur-md">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center gap-6">
          <span className="font-data text-xs text-slate-300 tracking-widest" data-testid="hero-slide-counter">
            <span className="text-[#E2C68C]">0{idx + 1}</span> / 0{SLIDES.length}
          </span>
          <div className="flex-1 grid grid-cols-3 gap-3 sm:gap-5">
            {SLIDES.map((sl, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`hero-slide-indicator-${i + 1}`} className="group text-left py-3" aria-label={`Slide ${i + 1}`}>
                <span className="block h-px bg-white/20 overflow-hidden">
                  {i === idx && (
                    <motion.span key={idx} className="block h-full bg-[#C5A059]" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: DURATION / 1000, ease: "linear" }} />
                  )}
                  {i < idx && <span className="block h-full w-full bg-[#C5A059]/50" />}
                </span>
                <span className={`hidden md:block mt-3 text-xs tracking-wide truncate transition-colors ${i === idx ? "text-slate-100" : "text-slate-400 group-hover:text-slate-200"}`}>
                  {sl.eyebrow}
                </span>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => go(-1)} data-testid="hero-slide-prev-button" aria-label="Previous slide" className="w-11 h-11 grid place-items-center border border-white/20 text-slate-100 hover:border-[#C5A059] hover:text-[#E2C68C] transition-colors duration-300">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button onClick={() => go(1)} data-testid="hero-slide-next-button" aria-label="Next slide" className="w-11 h-11 grid place-items-center border border-white/20 text-slate-100 hover:border-[#C5A059] hover:text-[#E2C68C] transition-colors duration-300">
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
